import React, { useEffect, useRef, useState } from 'react';
import Style from './Home.module.scss';
import { motion, useReducedMotion } from 'framer-motion';
import { info } from '../../info/Info';
import { StreamedWords } from '../ui/Reveal';
import CountUp from '../ui/CountUp';
import Magnetic from '../ui/Magnetic';
import { ArrowDown, ArrowUpRight, socialIconMap } from '../ui/Icons';

const VIDEO_SRC = process.env.PUBLIC_URL + '/video/hero.mp4';
const POSTER_SRC = process.env.PUBLIC_URL + '/video/hero-poster.jpg';

const lineReveal = {
    hidden: { y: '110%' },
    visible: (i) => ({
        y: 0,
        transition: { duration: 0.9, delay: 0.55 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
    }),
};

export default function Home({ innerRef, onOpenTerminal }) {
    const reduce = useReducedMotion();
    const [videoOk, setVideoOk] = useState(true);
    const videoRef = useRef(null);

    // Fall back cleanly to the poster / plain background if the video is missing
    useEffect(() => {
        if (reduce) setVideoOk(false);
    }, [reduce]);

    // Mobile autoplay: React can drop the `muted` DOM property (a known bug), which
    // makes iOS/Android refuse to autoplay. Force muted + call play() ourselves, and
    // retry on the first touch in case the browser blocked the initial attempt.
    useEffect(() => {
        const v = videoRef.current;
        if (!v || !videoOk) return;

        v.muted = true;
        v.defaultMuted = true;
        v.setAttribute('muted', '');
        v.playsInline = true;

        const tryPlay = () => {
            const p = v.play();
            if (p && typeof p.catch === 'function') p.catch(() => { /* blocked; wait for gesture */ });
        };

        tryPlay();
        v.addEventListener('canplay', tryPlay, { once: true });
        const onGesture = () => tryPlay();
        window.addEventListener('touchstart', onGesture, { once: true, passive: true });
        window.addEventListener('scroll', onGesture, { once: true, passive: true });

        return () => {
            v.removeEventListener('canplay', tryPlay);
            window.removeEventListener('touchstart', onGesture);
            window.removeEventListener('scroll', onGesture);
        };
    }, [videoOk]);

    return (
        <section id="top" ref={innerRef} className={Style.hero}>
            <div className={Style.media} aria-hidden="true">
                {videoOk ? (
                    <video
                        ref={videoRef}
                        className={Style.video}
                        src={VIDEO_SRC}
                        poster={POSTER_SRC}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        onError={() => setVideoOk(false)}
                    />
                ) : (
                    <div
                        className={Style.video}
                        style={{ backgroundImage: `url(${POSTER_SRC})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                    />
                )}
                <div className={Style.scrim} />
            </div>

            <div className={Style.content}>
                <div className={Style.boot}>
                    {info.bootChecks.map((check, i) => (
                        <motion.p
                            key={i}
                            initial={reduce ? false : { opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4, delay: 0.15 + i * 0.18 }}
                        >
                            <span className={Style.pass}>✓</span>
                            <span className={Style.bootLine}>{check.line}</span>
                            <span className={Style.bootDots} />
                            <span className={Style.pass}>{check.status}</span>
                            <span className={Style.bootMs}>{check.ms}</span>
                        </motion.p>
                    ))}
                    <motion.button
                        className={Style.bootHint}
                        onClick={onOpenTerminal}
                        initial={reduce ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.85 }}
                    >
                        $ press <span>⌘K</span> — this portfolio has an API
                    </motion.button>
                </div>

                <motion.p
                    className={Style.kicker}
                    initial={reduce ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                >
                    {info.position} — <a href={info.companyUrl} target="_blank" rel="noopener noreferrer">{info.company}</a>
                </motion.p>

                <h1 className={Style.name}>
                    <span className={Style.mask}>
                        <motion.span
                            className={Style.line}
                            custom={0}
                            initial={reduce ? 'visible' : 'hidden'}
                            animate="visible"
                            variants={lineReveal}
                        >
                            {info.firstName}
                        </motion.span>
                    </span>
                    <span className={Style.mask}>
                        <motion.span
                            className={`${Style.line} ${Style.italic}`}
                            custom={1}
                            initial={reduce ? 'visible' : 'hidden'}
                            animate="visible"
                            variants={lineReveal}
                        >
                            {info.lastName}
                        </motion.span>
                    </span>
                </h1>

                <StreamedWords words={info.tagline} delay={1.1} className={Style.tagline} />

                <motion.div
                    className={Style.ctas}
                    initial={reduce ? false : { opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 1.7 }}
                >
                    <Magnetic>
                        <a href="#work" className={Style.primaryBtn}>
                            view work <ArrowDown size={13} />
                        </a>
                    </Magnetic>
                    <Magnetic>
                        <a href={info.companyUrl} target="_blank" rel="noopener noreferrer" className={Style.ghostBtn}>
                            cognivox-ai.com <ArrowUpRight size={13} />
                        </a>
                    </Magnetic>
                    <div className={Style.socials}>
                        {info.socials.filter(s => s.icon !== 'globe').map((social) => {
                            const Icon = socialIconMap[social.icon];
                            return (
                                <a
                                    key={social.label}
                                    href={social.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={social.label}
                                    className={Style.socialLink}
                                >
                                    <Icon size={17} />
                                </a>
                            );
                        })}
                    </div>
                </motion.div>
            </div>

            <motion.div
                className={Style.metrics}
                initial={reduce ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 2 }}
            >
                {info.metrics.map((metric, i) => (
                    <div key={i} className={Style.metric}>
                        <span className={Style.metricValue}>
                            <CountUp value={metric.value} suffix={metric.suffix} />
                        </span>
                        <span className={Style.metricLabel}>{metric.label}</span>
                    </div>
                ))}
            </motion.div>
        </section>
    );
}
